<template>
    <div class="breadcrumb-container">
        <Button
            v-if="items.length > 1"
            icon="pi pi-chevron-left"
            class="up-button"
            text
            rounded
            @click="navigateUp"
        />
        <div v-else class="button-placeholder"></div>
        <ul class="breadcrumbs">
            <li><span class="crumb">&nbsp;</span></li>
            <li v-for="item in items" :key="item.description">
                <RouterLink
                    v-if="item.link && route.name !== item.link.name"
                    class="crumb"
                    :class="item.type"
                    :to="item.link"
                >
                    <span class="crumb-content">
                        <i :class="item.icon ? item.icon : 'fake-icon'"></i>
                        <span>{{ item.description }}</span>
                    </span>
                </RouterLink>
                <span
                    v-else
                    class="crumb"
                    :class="{
                        [item.type]: true,
                        current: route.name === item.link.name,
                    }"
                >
                    <span class="crumb-content">
                        <i :class="item.icon ? item.icon : 'fake-icon'"></i>
                        <span>{{ item.description }}</span>
                    </span>
                </span>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { ProcessElementType } from "@/lib/openapi/gen";
import { EntityType, getEntityInfo } from "@/stores/entity-store";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { computed } from "vue";
import { useRouter, RouterLink } from "vue-router";
import type { RouteLocation } from "vue-router";
import { useRoute } from "vue-router";

interface Crumb {
    icon?: string;
    description: string;
    link: RouteLocation;
    type: string;
}

const { organization } = useOrganizationManager();
const { stek } = useStekManager();

const {
    entity,
    domain,
    product,
    process,
    task,
    gateway,
    flow,
    event,
    artefact,
    role,
    businessEntity,
} = useCurrentEntityManager();

const route = useRoute();
const defaultParams = computed(() => ({
    orgName: organization.value?.name,
    stekName: stek.value?.name,
}));

const crumbs: {
    [key: string]: (id?: string, name?: string) => Crumb | undefined;
} = {
    domainPage: (id = domain.value?.identity, name = domain.value?.name) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-cloud",
            description: name,
            link: {
                name: "domainPage",
                params: {
                    ...defaultParams.value,
                    domainId: id,
                },
            },
            type: "biz",
        };
    },
    productPage: (id = product.value?.identity, name = product.value?.name) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-box",
            description: name,
            link: {
                name: "productPage",
                params: {
                    ...defaultParams.value,
                    productId: id,
                },
            },
            type: "biz",
        };
    },
    rolePage: (id = role.value?.identity, name = role.value?.name) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-id-card",
            description: name,
            link: {
                name: "rolePage",
                params: {
                    ...defaultParams.value,
                    roleId: id,
                },
            },
            type: "biz",
        };
    },
    businessEntityPage: (
        id = businessEntity.value?.identity,
        name = businessEntity.value?.name
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-building",
            description: name,
            link: {
                name: "businessEntityPage",
                params: {
                    ...defaultParams.value,
                    businessEntityId: id,
                },
            },
            type: "biz",
        };
    },
    processPage: (id = process.value?.identity, name = process.value?.name) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-share-alt",
            description: name,
            link: {
                ...defaultParams.value,
                name: "processPage",
                params: {
                    ...defaultParams.value,
                    processId: id,
                },
            },
            type: "biz",
        };
    },
    taskPage: (
        id = task.value?.identity,
        name = task.value?.name || task.value?.bpmn_id
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-check-square",
            description: name,
            link: {
                name: "processElementPage",
                params: {
                    ...defaultParams.value,
                    elementId: id,
                    elementType: ProcessElementType.TASK,
                },
            },
            type: "biz",
        };
    },
    gatewayPage: (
        id = gateway.value?.identity,
        name = gateway.value?.name || gateway.value?.bpmn_id
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-directions",
            description: name,
            link: {
                name: "processElementPage",
                params: {
                    ...defaultParams.value,
                    elementId: id,
                    elementType: ProcessElementType.GATEWAY,
                },
            },
            type: "biz",
        };
    },
    eventPage: (
        id = event.value?.identity,
        name = event.value?.name || event.value?.bpmn_id
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-stop-circle",
            description: name,
            link: {
                name: "processElementPage",
                params: {
                    ...defaultParams.value,
                    elementId: id,
                    elementType: ProcessElementType.EVENT,
                },
            },
            type: "biz",
        };
    },
    flowPage: (
        id = flow.value?.identity,
        name = flow.value?.name || flow.value?.bpmn_id
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-window-minimize",
            description: name,
            link: {
                name: "processElementPage",
                params: {
                    ...defaultParams.value,
                    elementId: id,
                    elementType: ProcessElementType.FLOW,
                },
            },
            type: "biz",
        };
    },
    documentPage: (
        id = artefact.value?.identity,
        name = artefact.value?.name
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-file",
            description: name || "Document",
            link: {
                name: route.name,
                params: {
                    artefactId: id,
                },
            },
            type: "biz",
        };
    },
    processDefinitionPage: (
        id = process.value?.identity,
        name = "Definition"
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            description: name,
            link: {
                name: "processDefinitionPage",
                params: {
                    ...defaultParams.value,
                    processId: id,
                },
            },
            type: "biz",
        };
    },
    processDefinitionHistoryPage: (
        id = process.value?.identity,
        name = "Definition"
    ) => {
        if (!id || !name) {
            return;
        }

        return {
            icon: "pi pi-history",
            description: name,
            link: {
                name: "processDefinitionHistoryPage",
                params: {
                    ...defaultParams.value,
                    processId: process.value.identity,
                },
            },
            type: "biz",
        };
    },
};

type EntityTypeToCrumb = {
    [key in EntityType]?: string;
};

const entityTypeToCrumb: EntityTypeToCrumb = {
    domain: "domainPage",
    product: "productPage",
    role: "rolePage",
    business_entity: "businessEntityPage",
    process: "processPage",
    task: "taskPage",
    event: "eventPage",
    gateway: "gatewayPage",
    flow: "flowPage",
    artefact: "documentPage",
};

// Crumbs that don't have a corresponding entity (perhaps they can be represented
// through a specific entity + attribute combination in the future)
const specialCrumbs = {
    process_definition: "processDefinitionPage",
};

const router = useRouter();

const navigateUp = () => {
    if (items.value.length > 1) {
        const target = items.value[items.value.length - 2];
        router.push(target.link);
    }
};

const items = computed<Crumb[]>(() => {
    if ("breadcrumbs" in route.meta && route.meta.breadcrumbs) {
        if (route.meta.breadcrumbs === "auto") {
            // Iterate through entity's ancestors to generate breadcrumbs
            let currentEntity = entity.value;
            const generatedCrumbs: Crumb[] = [];

            // Some pages have a special breadcrumb which doesn't directly correspond to an entity while still being
            // part of an entity's hierarchy.
            if (
                Object.values(specialCrumbs).find((name) => name === route.name)
            ) {
                const entityInfo = getEntityInfo(route.params);
                if (entityInfo?.entityId) {
                    const crumbFn = crumbs[String(route.name)];
                    const crumb = crumbFn(String(entityInfo.entityId));
                    if (crumb) {
                        generatedCrumbs.push(crumb);
                    }
                }
            }
            while (currentEntity) {
                const crumbName = entityTypeToCrumb[currentEntity.entity_type];
                const crumbFn = crumbs[String(crumbName)];
                if (crumbFn) {
                    const crumb = crumbFn(
                        currentEntity.identity,
                        currentEntity.name
                    );
                    if (crumb) {
                        generatedCrumbs.push(crumb);
                    } else {
                        throw new Error(
                            `Could not generate breadcrumb for ${currentEntity.entity_type} with id ${currentEntity.identity}`
                        );
                    }
                }
                currentEntity = currentEntity.parent;
            }
            return generatedCrumbs.toReversed();
        } else {
            return route.meta.breadcrumbs
                .map((key: string) => crumbs[key]())
                .filter((c) => c !== undefined); // Filter out undefined values
        }
    }
    return [];
});
</script>

<style scoped lang="scss">
.button-placeholder {
    width: 2.5rem; /* Same width as the button */
    height: 2.5rem; /* Same height as the button */
}

.breadcrumb-container {
    display: flex;
    align-items: center;
}

.up-button {
    z-index: 1;
}

.breadcrumbs {
    display: flex;
    position: relative;
    list-style: none;
    overflow: hidden;
    left: -5px;
    z-index: 0;

    li {
        float: left;

        .crumb {
            /* 30px: the width of the colored triangle
                5px: the width of the white triangle
                2px: probably some inset */
            left: calc(-30px + 5px + 2px);
            height: 100%;
            color: white;
            text-decoration: none;
            padding: 5px 0 5px 30px;
            background: grey;
            position: relative;
            display: block;
            float: left;

            &::before {
                content: " ";
                display: block;
                width: 0;
                height: 0;
                border-top: 50px solid transparent;
                border-bottom: 50px solid transparent;
                border-left: 30px solid var(--p-menubar-background);
                position: absolute;
                top: 50%;
                margin-top: -50px;
                /* The width of the white triangle */
                margin-left: 5px;
                left: calc(100% - 15px);
                z-index: 1;
            }

            &::after {
                content: " ";
                display: block;
                width: 0;
                height: 0;
                border-top: 50px solid transparent;
                border-bottom: 50px solid transparent;
                border-left: 30px solid grey;
                position: absolute;
                top: 50%;
                margin-top: -50px;
                left: calc(100% - 15px);
                z-index: 2;
            }

            &:not(.current) {
                &.biz {
                    background: var(--stekz-biz-color);

                    &::after {
                        border-left-color: var(--stekz-biz-color);
                    }
                }

                &.dev {
                    background: var(--stekz-dev-color);

                    &::after {
                        border-left-color: var(--stekz-dev-color);
                    }
                }
            }
        }
    }

    li:first-child {
        .crumb {
            padding: 5px 0 5px 10px;
        }

        /* To make the colored arrow disappear for the first fake item in the breadcrumb */
        .crumb::after {
            content: none;
        }
    }

    .crumb-content {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        position: relative;
        z-index: 3;
        white-space: nowrap;

        .fake-icon {
            height: 14px;
        }
    }
}
</style>
